// Training titles and beats (E01-S3-T4; contracts 1.0 section 5).
import { z } from "zod";
import { Citation, Pages } from "./citation.js";
import { Id, IsoUtc, NonEmpty, strictObject, Ulid, version } from "./primitives.js";
import { Audience } from "./sop.js";
import { SopStepRef } from "./tripwire.js";
/** A SHA-256 digest: 64 lower-case hex characters. */
export const Sha256 = z
    .string()
    .regex(/^[0-9a-f]{64}$/, "expected a sha256: 64 lower-case hex characters");
export const TitleFormat = z.enum(["MX", "GW", "DD", "SIM", "CB", "AO", "FS"]);
export const TitleTier = z.enum(["Foundation", "Core", "Efficiency", "Situational", "Oversight"]);
const Seconds = z.number().int().positive();
export const TrainingTitle = strictObject({
    v: version(1),
    id: Id,
    title: NonEmpty,
    library: NonEmpty,
    format: TitleFormat,
    tier: TitleTier,
    contentVersion: NonEmpty,
    runtimeSeconds: Seconds.optional(),
    plannedRuntimeSeconds: Seconds.optional(),
    audience: z.array(Audience).min(1),
    covers: NonEmpty,
    description: NonEmpty,
    watchFor: NonEmpty.optional(),
    media: strictObject({ key: NonEmpty, sha256: Sha256, captions: NonEmpty.optional() }).optional(),
    completeAtPct: z.number().gt(0).max(100).optional(), // the reader applies 90 when absent; parse is identity
    requiresAttestation: z.boolean().optional(),
    requiredFor: z.array(NonEmpty).optional(),
    triggers: strictObject({
        screens: z.array(Id).optional(),
        overlays: z.array(Id).optional(),
        sopIds: z.array(Id).optional(),
    }).optional(),
    guidePages: Pages,
    // ADR-036 (1.6): what the player's intro card shows beside the title. The build fills section from
    // the pack's production.yaml and guideEdition from pack.yaml's source.docVersion; the served title
    // carries versionNumber and publishedAt when a tenant version is published, and neither for the
    // pack's edition. All optional: a title signed before 1.6 has none, and no consumer must read them.
    section: NonEmpty.max(120).optional(),
    guideEdition: NonEmpty.optional(),
    versionNumber: z.number().int().positive().optional(),
    publishedAt: IsoUtc.optional(),
}).superRefine((t, ctx) => {
    const bad = (path, message) => {
        ctx.addIssue({ code: "custom", message: `title ${t.id}: ${message}`, path: [path] });
    };
    if (t.media !== undefined) {
        if (t.runtimeSeconds === undefined)
            bad("runtimeSeconds", "a title with media carries runtimeSeconds, measured from the file (D-75)");
        if (t.plannedRuntimeSeconds !== undefined)
            bad("plannedRuntimeSeconds", "plannedRuntimeSeconds is for a title with no media (D-75)");
    }
    else {
        if (t.runtimeSeconds !== undefined)
            bad("runtimeSeconds", "runtimeSeconds is measured from media; a title with none carries plannedRuntimeSeconds (D-75)");
        if (t.plannedRuntimeSeconds === undefined)
            bad("plannedRuntimeSeconds", "a title with no media carries plannedRuntimeSeconds (D-75)");
    }
});
export const Beat = strictObject({
    v: version(1),
    id: Id,
    titleId: Id,
    start: z.number().nonnegative(),
    end: z.number().nonnegative(),
    label: NonEmpty,
    teaches: strictObject({
        sopStep: SopStepRef.optional(),
        field: Id.optional(),
        tripwire: Id.optional(),
        target: Id.optional(),
    }).refine(
    // A key can be present and undefined, which the inferred type does not show.
    (t) => Object.values(t).some((x) => x !== undefined), "a beat teaches at least one thing"),
    citation: Citation,
}).superRefine((b, ctx) => {
    if (!(b.start < b.end))
        ctx.addIssue({ code: "custom", message: `beat ${b.id}: start is before end`, path: ["end"] });
});
export const LearningPath = strictObject({
    id: Id,
    title: NonEmpty,
    audience: z.array(Audience).min(1),
    titleIds: z.array(Id).min(1),
}).superRefine((p, ctx) => {
    if (new Set(p.titleIds).size !== p.titleIds.length)
        ctx.addIssue({
            code: "custom",
            message: `path ${p.id}: a title appears once`,
            path: ["titleIds"],
        });
});
export const Completion = strictObject({
    userId: NonEmpty,
    titleId: Id,
    titleVersion: NonEmpty,
    watchedPct: z.number().min(0).max(100),
    completedAt: IsoUtc.optional(),
    attestedAt: IsoUtc.optional(),
    // ADR-023: the passed attempt that counts toward completion, against the served contentVersion.
    check: strictObject({
        attemptId: Ulid,
        scorePct: z.number().int().min(0).max(100),
        passedAt: IsoUtc,
    }).optional(),
});
//# sourceMappingURL=training.js.map